// ---------------------------------------------------------------------------
// CANDIDATE ATTRITION — what was thrown away before scoring ever ran.
//
// Morgan, Aug 22: "generated candidates → dedupe → backtrack → coherence →
// eligibility/other gates → scoring pool... the specific rule and measured
// value that caused each rejection."
//
// Written because the 22 Aug ablation replay showed `scored=4 → dedupe=2 →
// coherence=1` on corridor after corridor. Eleven scoring ablations changed
// nothing because there was one candidate left to pick. No scoring rule,
// floor or ceiling can matter when the pool has one member.
//
// Recording only. Nothing here filters, scores or reorders anything.
// ---------------------------------------------------------------------------

export interface Rejection {
  stage: string;
  /**
   * WHICH classifyRoutes pass recorded this. classifyThreeCards runs the whole
   * pipeline three times (direct / pleasant / scenic) at different tolerances,
   * so without this every rejection is counted three times over and the ledger
   * cannot close. Empty in the offline harnesses, which call classifyRoutes
   * exactly once.
   */
  pass?: string;
  /** Longest-first named roads — what a person can actually judge. */
  roads: string;
  source: string;
  /** The gate that killed it, decomposed — never "coherence". */
  rule: string;
  /** What was measured, and what it had to beat. Both, or neither is useful. */
  measured?: number;
  threshold?: number;
  durationMin?: number;
  /**
   * The discarded candidate itself, so a sweep can read the score it ALREADY
   * received rather than re-scoring it or re-deriving why it died. Typed
   * `unknown` to keep attrition.ts free of a scoring import. Only populated
   * while attrition is enabled, so production never retains a route here.
   */
  candidate?: unknown;
}

/**
 * A gate reading for ONE candidate, recorded whether it passed or failed.
 * A threshold can only be judged against the distribution it sits in.
 */
export interface Measurement {
  rule: string;
  value: number;
  threshold: number;
  passed: boolean;
  roads: string;
}

export interface StageCount {
  stage: string;
  /** See Rejection.pass. */
  pass?: string;
  entering: number;
  leaving: number;
}

export interface AttritionTrace {
  corridor: string;
  generated: number;
  stages: StageCount[];
  rejections: Rejection[];
  measurements: Measurement[];
  /** Nothing left to choose between — scoring is decorative. */
  onlyOneSurvivor: boolean;
  /** Every candidate rejected, then something restored to avoid a blank UI. */
  zeroSurvivedThenBackfilled: boolean;
  finalPleasant?: string;
  finalScenic?: string;
}

let CURRENT: AttritionTrace | null = null;
const COMPLETED: AttritionTrace[] = [];
/** Off by default. Production pays nothing until a shadow run turns it on. */
let ENABLED = false;

let PASS = "";
/** Label the classifyRoutes pass that follows. Reset with each new trace. */
export function setAttritionPass(p: string): void { PASS = p; }

export function enableAttrition(on: boolean): void { ENABLED = on; if (!on) CURRENT = null; }
export function attritionEnabled(): boolean { return ENABLED; }

export function beginAttrition(corridor: string, generated: number): void {
  if (!ENABLED) return;
  PASS = "";
  CURRENT = {
    corridor, generated, stages: [], rejections: [], measurements: [],
    onlyOneSurvivor: false, zeroSurvivedThenBackfilled: false,
  };
}

export function recordStage(stage: string, entering: number, leaving: number): void {
  if (!ENABLED || !CURRENT) return;
  CURRENT.stages.push({ stage, entering, leaving, pass: PASS || undefined });
  if (leaving === 0) CURRENT.zeroSurvivedThenBackfilled = true;
  if (leaving === 1) CURRENT.onlyOneSurvivor = true;
}

export function recordRejection(r: Rejection): void {
  if (!ENABLED || !CURRENT) return;
  CURRENT.rejections.push({ ...r, pass: r.pass ?? (PASS || undefined) });
}

export function recordMeasurement(m: Measurement): void {
  if (!ENABLED || !CURRENT) return;
  CURRENT.measurements.push(m);
}

export function endAttrition(finalPleasant?: string, finalScenic?: string): AttritionTrace | null {
  if (!ENABLED || !CURRENT) return null;
  CURRENT.finalPleasant = finalPleasant;
  CURRENT.finalScenic = finalScenic;
  COMPLETED.push(CURRENT);
  const done = CURRENT;
  CURRENT = null;
  return done;
}

export function completedTraces(): AttritionTrace[] { return COMPLETED; }
export function clearTraces(): void { COMPLETED.length = 0; }

/** Longest-first named roads from a route's steps. */
export function roadsOfSteps(
  steps: Array<{ distance: number; roadName?: string }> | undefined,
  take = 4,
): string {
  const by = new Map<string, number>();
  for (const s of steps ?? []) {
    const n = (s.roadName ?? "").trim();
    if (n) by.set(n, (by.get(n) ?? 0) + s.distance);
  }
  return [...by.entries()].sort((a, b) => b[1] - a[1]).slice(0, take)
    .map(([n, m]) => `${n} (${(m / 1609.34).toFixed(1)}mi)`).join(" · ") || "(unnamed)";
}

export function formatTrace(t: AttritionTrace): string {
  const out: string[] = [];
  out.push(`\n${t.corridor}   generated ${t.generated}`);
  for (const s of t.stages) {
    const lost = s.entering - s.leaving;
    out.push(`  ${(s.pass ? s.pass + "/" : "") + s.stage}`.padEnd(26) +
      `${String(s.entering).padStart(3)} → ${String(s.leaving).padStart(3)}` +
      (lost ? `   lost ${lost}` : ``));
  }
  if (t.rejections.length) {
    out.push(`  REJECTED:`);
    for (const r of t.rejections) {
      const m = r.measured !== undefined && r.threshold !== undefined
        ? `  measured ${r.measured.toFixed(3)} vs threshold ${r.threshold.toFixed(3)}`
        : r.measured !== undefined ? `  measured ${r.measured.toFixed(3)}` : ``;
      out.push(`    [${r.pass ? r.pass + ":" : ""}${r.stage}/${r.rule}]${m}`);
      out.push(`        ${r.roads}   (${r.source}${r.durationMin !== undefined ? `, ${r.durationMin.toFixed(0)} min` : ``})`);
    }
  }
  if (t.zeroSurvivedThenBackfilled)
    out.push(`  ⚠ ZERO SURVIVED a stage — something was backfilled to avoid a blank result.`);
  else if (t.onlyOneSurvivor)
    out.push(`  ⚠ ONE CANDIDATE reached ranking. Scoring had nothing to choose between.`);
  out.push(`  → Pleasant: ${t.finalPleasant ?? "(none)"}`);
  out.push(`  → Scenic:   ${t.finalScenic ?? "(none)"}`);
  return out.join("\n");
}

export function formatAttritionSummary(traces: AttritionTrace[]): string {
  const n = traces.length || 1;
  const one = traces.filter((t) => t.onlyOneSurvivor).length;
  const zero = traces.filter((t) => t.zeroSurvivedThenBackfilled).length;
  const byRule = new Map<string, number>();
  for (const t of traces) for (const r of t.rejections)
    byRule.set(`${r.stage}/${r.rule}`, (byRule.get(`${r.stage}/${r.rule}`) ?? 0) + 1);
  const gen = traces.reduce((a, t) => a + t.generated, 0);
  const survived = traces.reduce((a, t) => a + (t.stages.at(-1)?.leaving ?? 0), 0);

  // LEDGER RECONCILIATION (Morgan, Aug 22). Every candidate that disappears
  // must be named by a rejection, and every candidate that reappears must be
  // named by a stage. If the arithmetic does not close, a gate is removing
  // routes with no confession — and no attrition percentage quoted from this
  // summary can be trusted until it does.
  const rejected = traces.reduce((a, t) => a + t.rejections.length, 0);
  const backfilled = traces.reduce(
    (a, t) => a + (t.stages.find((s) => s.stage === "keep-one-fallback")?.leaving ?? 0), 0);
  const expected = gen - rejected + backfilled;

  const lines = [
    ``,
    `ATTRITION  ${traces.length} corridors`,
    `  generated ${gen} candidates, ${survived} reached ranking  (${((100 * survived) / (gen || 1)).toFixed(0)}% survived)`,
    `  corridors where ONE candidate reached ranking     ${String(one).padStart(4)}  ${((100 * one) / n).toFixed(0)}%`,
    `  corridors where a stage emptied the pool          ${String(zero).padStart(4)}  ${((100 * zero) / n).toFixed(0)}%`,
    ``,
  ];
  if (expected !== survived) {
    lines.push(
      `  ⚠ LEDGER DOES NOT RECONCILE — generated ${gen} − rejections ${rejected}` +
      ` + backfilled ${backfilled} = ${expected}, but ${survived} reached ranking.` +
      ` ${Math.abs(expected - survived)} candidate(s) change state with no stage or rejection recorded.` +
      ` Do not quote attrition percentages until this closes.`,
      ``,
    );
  }
  lines.push(`  rejections by rule:`);
  for (const [rule, k] of [...byRule].sort((a, b) => b[1] - a[1]))
    lines.push(`    ${String(k).padStart(4)}  ${rule}`);
  lines.push(``);
  lines.push(`  A corridor with one survivor is not a routing decision. It is a`);
  lines.push(`  default wearing the Pleasant label.`);
  lines.push(``);
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// GATE DISTRIBUTION — where the threshold sits inside the data it is judging.
//
// A rejection list alone cannot answer "is 0.015 the right number". It shows
// only the values above the line. This prints the whole distribution, so the
// line can be seen against the body of the measurements rather than against
// the tail it removed.
// ---------------------------------------------------------------------------
export function formatGateDistribution(traces: AttritionTrace[]): string {
  const byRule = new Map<string, Measurement[]>();
  for (const t of traces)
    for (const m of t.measurements) {
      const arr = byRule.get(m.rule) ?? [];
      arr.push(m);
      byRule.set(m.rule, arr);
    }
  if (!byRule.size) return "\n(no gate measurements recorded)";

  const out: string[] = ["", "=".repeat(78),
    "GATE DISTRIBUTIONS  (every candidate measured, pass and fail)"];
  for (const [rule, ms] of [...byRule].sort()) {
    const vals = ms.map((m) => m.value).sort((x, y) => x - y);
    const thr = ms[0].threshold;
    const q = (f: number) => vals[Math.min(vals.length - 1, Math.floor(f * vals.length))];
    const failed = ms.filter((m) => !m.passed).length;
    out.push(
      ``,
      `  ${rule}   threshold ${thr}`,
      `    n ${vals.length}   failed ${failed} (${((100 * failed) / vals.length).toFixed(0)}%)`,
      `    min ${vals[0].toFixed(4)}   p25 ${q(0.25).toFixed(4)}   median ${q(0.5).toFixed(4)}` +
        `   p75 ${q(0.75).toFixed(4)}   p90 ${q(0.9).toFixed(4)}   max ${vals[vals.length - 1].toFixed(4)}`,
    );
    // Ten buckets from 0 to max, with the threshold marked, so the shape of
    // the distribution and the position of the line are visible together.
    const max = vals[vals.length - 1] || 1;
    const B = 10;
    const counts = new Array(B).fill(0);
    for (const v of vals) counts[Math.min(B - 1, Math.floor((v / max) * B))]++;
    const widest = Math.max(...counts);
    for (let i = 0; i < B; i++) {
      const lo = (i * max) / B;
      const hi = ((i + 1) * max) / B;
      const mark = thr >= lo && thr < hi ? "  <== THRESHOLD" : "";
      out.push(
        `      ${lo.toFixed(4)}-${hi.toFixed(4)}  ` +
        `${"#".repeat(Math.round((counts[i] / (widest || 1)) * 30)).padEnd(30)} ${counts[i]}${mark}`,
      );
    }
  }
  return out.join("\n");
}
